// ===========================================
// Avatar Picker Dialog Component
// ===========================================
// Allows students to select their avatar from character mascots

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { Check as CheckIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import {
  getAgeGroupFromBirthDate,
  getCharacterColor,
  getCharacterName,
  type AgeGroup,
} from '../brand';

// Avatar data organized by character
const AVATARS_BY_CHARACTER: Record<string, string[]> = {
  alice: ['drums', 'guitar', 'happy', 'maracas', 'microphone', 'piano', 'singing', 'standing'],
  steve: ['drums', 'guitar', 'happy', 'microphone', 'piano', 'singing', 'standing', 'walking'],
  liam: ['drums', 'guitar', 'happy', 'microphone', 'piano', 'singing', 'standing', 'walking'],
  floyd: ['drums', 'guitar', 'happy', 'microphone', 'piano', 'singing', 'standing', 'walking'],
};

const CHARACTER_TO_AGE_GROUP: Record<string, AgeGroup> = {
  alice: 'PRESCHOOL',
  steve: 'KIDS',
  liam: 'TEENS',
  floyd: 'ADULT',
};

const CHARACTER_ORDER = ['alice', 'steve', 'liam', 'floyd'];

interface AvatarPickerDialogProps {
  open: boolean;
  onClose: () => void;
  currentAvatar: string | null;
  birthDate: string;
  onSelect: (avatar: string | null) => void;
  isLoading?: boolean;
}

export function AvatarPickerDialog({
  open,
  onClose,
  currentAvatar,
  birthDate,
  onSelect,
  isLoading,
}: AvatarPickerDialogProps) {
  // Get default character based on age
  const defaultAgeGroup = getAgeGroupFromBirthDate(birthDate);
  const defaultCharacter = getCharacterName(defaultAgeGroup).toLowerCase();

  // State for selected character tab
  const [selectedCharacter, setSelectedCharacter] = useState(defaultCharacter);

  // State for selected avatar within the dialog
  const [pendingAvatar, setPendingAvatar] = useState<string | null>(currentAvatar);

  // Reset pending avatar when dialog opens
  useEffect(() => {
    if (open) {
      setPendingAvatar(currentAvatar);
      // Set tab to current avatar's character or default
      if (currentAvatar) {
        const char = currentAvatar.split('/')[0];
        if (CHARACTER_ORDER.includes(char)) {
          setSelectedCharacter(char);
        }
      } else {
        setSelectedCharacter(defaultCharacter);
      }
    }
  }, [open, currentAvatar, defaultCharacter]);

  const handleSave = () => {
    onSelect(pendingAvatar);
  };

  const handleReset = () => {
    setPendingAvatar(null);
  };

  const avatars = AVATARS_BY_CHARACTER[selectedCharacter] || [];
  const characterColor = getCharacterColor(CHARACTER_TO_AGE_GROUP[selectedCharacter]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Choose Your Avatar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pick a character pose that represents you!
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Character Tabs */}
        <Tabs
          value={selectedCharacter}
          onChange={(_, val) => setSelectedCharacter(val)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="fullWidth"
        >
          {CHARACTER_ORDER.map((char) => {
            const ageGroup = CHARACTER_TO_AGE_GROUP[char];
            const color = getCharacterColor(ageGroup);
            // Show "(You)" next to the character of the current saved avatar, or default if no avatar
            const currentAvatarChar = currentAvatar ? currentAvatar.split('/')[0] : null;
            const isCurrentCharacter = currentAvatarChar ? char === currentAvatarChar : char === defaultCharacter;
            return (
              <Tab
                key={char}
                value={char}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: color,
                      }}
                    />
                    {getCharacterName(ageGroup)}
                    {isCurrentCharacter && (
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.7, ml: 0.5, fontSize: '0.65rem' }}
                      >
                        (You)
                      </Typography>
                    )}
                  </Box>
                }
                sx={{
                  '&.Mui-selected': { color },
                  textTransform: 'none',
                }}
              />
            );
          })}
        </Tabs>

        {/* Avatar Grid */}
        <Grid container spacing={2}>
          {avatars.map((pose) => {
            const avatarPath = `${selectedCharacter}/${selectedCharacter}-${pose}.svg`;
            const isSelected = pendingAvatar === avatarPath;

            return (
              <Grid item xs={6} sm={4} md={3} key={pose}>
                <Paper
                  elevation={isSelected ? 4 : 1}
                  onClick={() => setPendingAvatar(avatarPath)}
                  sx={{
                    p: 1,
                    cursor: 'pointer',
                    border: isSelected ? `3px solid ${characterColor}` : '3px solid transparent',
                    bgcolor: isSelected ? `${characterColor}15` : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 3,
                    },
                    position: 'relative',
                  }}
                >
                  {isSelected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: characterColor,
                        borderRadius: '50%',
                        p: 0.5,
                        display: 'flex',
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                    </Box>
                  )}
                  <Box
                    component="img"
                    src={`/avatars/${avatarPath}`}
                    alt={`${selectedCharacter} ${pose}`}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      aspectRatio: '1',
                      objectFit: 'contain',
                    }}
                  />
                  <Typography
                    variant="caption"
                    align="center"
                    display="block"
                    sx={{ textTransform: 'capitalize', mt: 0.5 }}
                  >
                    {pose}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleReset}
          color="inherit"
          disabled={!pendingAvatar || isLoading}
          startIcon={<ResetIcon />}
        >
          Use Default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isLoading ? 'Saving...' : 'Save Avatar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AvatarPickerDialog;
